import React, { useEffect, useMemo, useState } from 'react';
import '@fontsource/lato';
import { getUsersForPractice } from '~/api/settings/getUsersForPractice';
import { Checkbox, Chip, Divider, IconButton, InputAdornment, Menu, MenuItem, Popover, TextField, } from '@mui/material';
import Button from '@mui/material/Button';
import { ArrowDropDownOutlined, FilterAltOutlined, PersonOutlined, SearchOutlined, } from '@mui/icons-material';
const roleOptions = [
    { value: '3', label: 'Provider' },
    { value: '4', label: 'Diabetes Educator/Dietitian' },
    { value: '5', label: 'Nurse' },
    { value: '6', label: 'Medical Assistant' },
    { value: '7', label: 'Office Manager' },
    { value: '8', label: 'Administrator' },
    { value: '9', label: 'Billing' },
    { value: '10', label: 'Other' },
    { value: '11', label: 'Clinical Account Manager' },
    { value: '12', label: 'Clinical Diabetes Specialist' },
    { value: '13', label: 'Patient Engagement & Account Manager' },
    { value: '14', label: 'Patient Engagement Specialist' },
];
const roleIdToLabel = (roleId) => {
    if (roleId == null)
        return '';
    const match = roleOptions.find(r => r.value === String(roleId));
    return match?.label ?? '';
};
const getField = (obj, ...keys) => {
    for (const k of keys) {
        if (obj && obj[k] != null && obj[k] !== '')
            return obj[k];
    }
    return undefined;
};
const getTeam = (user) => {
    const t = getField(user.personalInfo, 'team_name', 'teamName');
    return t || 'Other';
};
const getRoleId = (user) => getField(user.personalInfo, 'role_id', 'roleId');
const getOrg = (user) => user.personalInfo.accessLevel === 'SweetSpot' ? 'SweetSpot Staff' : 'Practice Staff';
const getFullName = (user) => `${user.personalInfo.firstname} ${user.personalInfo.lastname}`;
const QUICK_SELECT_TEAMS = ['Admin', 'Billing', 'Clinical', 'Device Troubleshooting', 'Enrollment', 'Other'];
const ORG_OPTIONS = ['SweetSpot Staff', 'Practice Staff'];
const TEAM_OPTIONS = ['Enrollment', 'Device Troubleshooting', 'Clinical', 'Admin', 'Billing', 'Other'];
export const UserSelectorLayout = (props) => {
    const [users, setUsers] = useState([]);
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const [filterAnchorEl, setFilterAnchorEl] = useState(null);
    const filterOpen = Boolean(filterAnchorEl);
    const [query, setQuery] = useState('');
    const [selectedFilters, setSelectedFilters] = useState(['Show Inactive Users']);
    const [selectedOrgFilters, setSelectedOrgFilters] = useState([...ORG_OPTIONS]);
    const [selectedTeamFilters, setSelectedTeamFilters] = useState([...TEAM_OPTIONS]);
    const [activeQuickSelect, setActiveQuickSelect] = useState(null);
    useEffect(() => {
        fetchUsers();
    }, []);
    const fetchUsers = async () => {
        const { users } = await getUsersForPractice();
        const usersArray = users.map((user) => ({
            id: user.id,
            email: user.email,
            personalInfo: user.personalInfo,
            affiliations: [],
        }));
        usersArray.sort((a, b) => a.personalInfo.lastname.localeCompare(b.personalInfo.lastname));
        setUsers(usersArray);
        const allUserIds = usersArray.map((u) => u.id);
        if (props.initialSelectedUser) {
            setSelectedUserIds(props.initialSelectedUser);
            props.setSelectedUsers(props.initialSelectedUser);
        }
        else {
            setSelectedUserIds(allUserIds);
            props.setSelectedUsers(allUserIds);
        }
    };
    // personName derived from selection — single source of truth.
    const personName = useMemo(() => users.filter(u => selectedUserIds.includes(u.id)).map(getFullName), [users, selectedUserIds]);
    // Visible list: search + Show Inactive + org + team.
    const filteredUsers = useMemo(() => {
        const showInactive = selectedFilters.includes('Show Inactive Users');
        const q = query.trim().toLowerCase();
        return users.filter((user) => {
            const { firstname, lastname, inviteStatus } = user.personalInfo;
            if (!showInactive && inviteStatus === 'Inactive')
                return false;
            if (q && !`${firstname} ${lastname}`.toLowerCase().includes(q))
                return false;
            if (!selectedOrgFilters.includes(getOrg(user)))
                return false;
            if (!selectedTeamFilters.includes(getTeam(user)))
                return false;
            return true;
        });
    }, [users, query, selectedFilters, selectedOrgFilters, selectedTeamFilters]);
    // Recompute whether the current selection still matches a quick-select team.
    // The chip stays active as long as selection equals (that team's users ∩ allowed orgs).
    useEffect(() => {
        if (!activeQuickSelect)
            return;
        const expected = new Set(users
            .filter(u => getTeam(u) === activeQuickSelect && selectedOrgFilters.includes(getOrg(u)))
            .map(u => u.id));
        const current = new Set(selectedUserIds);
        const sameSize = expected.size === current.size;
        const sameMembers = sameSize && [...expected].every(id => current.has(id));
        if (!sameMembers)
            setActiveQuickSelect(null);
    }, [selectedUserIds, selectedOrgFilters, users, activeQuickSelect]);
    const handleSelectAll = () => {
        const visibleIds = filteredUsers.map(u => u.id);
        setSelectedUserIds(prev => Array.from(new Set([...prev, ...visibleIds])));
    };
    const handleDeselectAll = () => {
        const visibleIds = new Set(filteredUsers.map(u => u.id));
        setSelectedUserIds(prev => prev.filter(id => !visibleIds.has(id)));
    };
    // Manual user toggle — breaks any active quick-select pattern.
    const handleUserSelect = (user) => {
        setSelectedUserIds(prev => prev.includes(user.id) ? prev.filter(id => id !== user.id) : [...prev, user.id]);
    };
    const handleFilterSelect = (filter) => {
        setSelectedFilters(prev => prev.includes(filter) ? prev.filter(f => f !== filter) : [...prev, filter]);
    };
    // Org toggle: hide+deselect on uncheck, show+select on recheck.
    // If a quick-select is active, only add back users on that team.
    const handleOrgFilterToggle = (org) => {
        const isRemoving = selectedOrgFilters.includes(org);
        const nextOrgFilters = isRemoving
            ? selectedOrgFilters.filter(f => f !== org)
            : [...selectedOrgFilters, org];
        setSelectedOrgFilters(nextOrgFilters);
        const usersInOrg = users.filter(u => getOrg(u) === org);
        if (isRemoving) {
            const ids = new Set(usersInOrg.map(u => u.id));
            setSelectedUserIds(prev => prev.filter(id => !ids.has(id)));
        }
        else {
            // Re-add: respect quick-select if active (only that team), otherwise add all users in this org.
            const toAdd = activeQuickSelect
                ? usersInOrg.filter(u => getTeam(u) === activeQuickSelect).map(u => u.id)
                : usersInOrg.map(u => u.id);
            setSelectedUserIds(prev => Array.from(new Set([...prev, ...toAdd])));
        }
    };
    // Team toggle: hide+deselect on uncheck, show+select on recheck.
    // Unchecking a team while its quick-select is active will clear the chip (handled by the reconcile effect).
    const handleTeamFilterToggle = (team) => {
        const isRemoving = selectedTeamFilters.includes(team);
        const nextTeamFilters = isRemoving
            ? selectedTeamFilters.filter(f => f !== team)
            : [...selectedTeamFilters, team];
        setSelectedTeamFilters(nextTeamFilters);
        const usersOnTeam = users.filter(u => getTeam(u) === team);
        if (isRemoving) {
            const ids = new Set(usersOnTeam.map(u => u.id));
            setSelectedUserIds(prev => prev.filter(id => !ids.has(id)));
        }
        else {
            // Re-add only users whose org is currently allowed.
            const toAdd = usersOnTeam
                .filter(u => selectedOrgFilters.includes(getOrg(u)))
                .map(u => u.id);
            setSelectedUserIds(prev => Array.from(new Set([...prev, ...toAdd])));
        }
    };
    // Quick Select: makes selection = that team's users (within allowed orgs).
    // Also flips Team filter checkboxes so only this team is checked.
    const handleQuickSelect = (team) => {
        setSelectedTeamFilters([team]);
        const matching = users
            .filter(u => getTeam(u) === team && selectedOrgFilters.includes(getOrg(u)));
        setSelectedUserIds(matching.map(u => u.id));
        setActiveQuickSelect(team);
    };
    const updateSelectedUsers = () => {
        props.setSelectedUsers(selectedUserIds);
        setAnchorEl(null);
    };
    const totalCount = filteredUsers.length;
    const selectedVisibleCount = filteredUsers.filter(u => selectedUserIds.includes(u.id)).length;
    return (React.createElement(React.Fragment, null,
        React.createElement(Button, { style: {
                backgroundColor: 'white',
                height: '30px',
                borderRadius: '4px',
                borderColor: '#E4E4E7',
                borderWidth: '2px',
            }, endIcon: React.createElement(ArrowDropDownOutlined, null), onClick: (event) => setAnchorEl(event.currentTarget) }, personName.length === users.length ? 'All Users' : personName.join(', ')),
        React.createElement(Popover, { anchorEl: anchorEl, open: open, onClose: () => setAnchorEl(null), anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' }, PaperProps: {
                style: {
                    width: 580,
                    maxHeight: '90vh',
                    display: 'flex',
                    flexDirection: 'column',
                    fontFamily: 'Lato, sans-serif',
                },
            } },
            React.createElement("div", { style: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                } },
                React.createElement("div", { style: { display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 700 } },
                    React.createElement(PersonOutlined, { style: { color: '#0000008A' } }),
                    React.createElement("span", { style: { color: '#0A0A0A', fontWeight: 500, fontSize: 18 } }, "Select Users")),
                React.createElement(Button, { variant: "contained", style: { backgroundColor: '#2C78EB', color: '#FFFFFF' }, onClick: updateSelectedUsers }, "Apply")),
            React.createElement(Divider, null),
            React.createElement("div", { style: { display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px' } },
                React.createElement(TextField, { fullWidth: true, size: "small", variant: "outlined", placeholder: "Search here", value: query, onChange: (e) => setQuery(e.target.value), InputProps: {
                        endAdornment: (React.createElement(InputAdornment, { position: "end" },
                            React.createElement(SearchOutlined, null))),
                    } }),
                React.createElement(IconButton, { onClick: (event) => setFilterAnchorEl(event.currentTarget), style: { border: '1px solid #E4E4E7', borderRadius: 4 } },
                    React.createElement(FilterAltOutlined, { style: { color: '#2C78EB' } })),
                React.createElement(Menu, { anchorEl: filterAnchorEl, open: filterOpen, onClose: () => setFilterAnchorEl(null) }, ['Show Inactive Users'].map(filter => (React.createElement(MenuItem, { key: filter, onClick: () => handleFilterSelect(filter) },
                    React.createElement(Checkbox, { checked: selectedFilters.includes(filter) }),
                    filter))))),
            React.createElement("div", { style: { padding: '0 16px 12px' } },
                React.createElement("div", { style: { fontSize: 14, color: '#717182', marginBottom: 8, fontFamily: 'Barlow' } }, "Quick Select:"),
                React.createElement("div", { style: { display: 'flex', flexWrap: 'wrap', gap: 8 } }, QUICK_SELECT_TEAMS.map(team => {
                    const active = activeQuickSelect === team;
                    return (React.createElement(Chip, { key: team, label: team, onClick: () => handleQuickSelect(team), style: {
                            backgroundColor: active ? '#2C78EB' : '#ECEEF2',
                            color: active ? '#FFFFFF' : '#030213',
                            cursor: 'pointer',
                            fontFamily: 'Barlow'
                        } }));
                }))),
            React.createElement("div", { style: { display: 'flex', gap: 16, padding: '0 16px 12px' } },
                React.createElement("div", { style: { flex: 1 } },
                    React.createElement("div", { style: { fontWeight: 700, marginBottom: 4, fontFamily: 'Barlow', color: '#0A0A0A' } }, "Organization"),
                    ORG_OPTIONS.map(org => (React.createElement("div", { key: org, style: { display: 'flex', alignItems: 'center', cursor: 'pointer' }, onClick: () => handleOrgFilterToggle(org) },
                        React.createElement(Checkbox, { checked: selectedOrgFilters.includes(org), size: "small" }),
                        React.createElement("span", { style: { color: '#0A0A0A', fontFamily: 'Barlow' } }, org))))),
                React.createElement("div", { style: { flex: 1 } },
                    React.createElement("div", { style: { fontWeight: 700, marginBottom: 4 } }, "Team"),
                    TEAM_OPTIONS.map(team => (React.createElement("div", { key: team, style: { display: 'flex', alignItems: 'center', cursor: 'pointer' }, onClick: () => handleTeamFilterToggle(team) },
                        React.createElement(Checkbox, { checked: selectedTeamFilters.includes(team), size: "small" }),
                        React.createElement("span", { style: { color: '#0A0A0A', fontFamily: 'Barlow' } }, team)))))),
            React.createElement(Divider, null),
            React.createElement("div", { style: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '12px 16px',
                } },
                React.createElement("div", { style: { color: '#52525B' } },
                    selectedVisibleCount,
                    " of ",
                    totalCount,
                    " selected"),
                React.createElement("div", { style: { display: 'flex', gap: 16 } },
                    React.createElement("span", { style: { color: '#18181B', cursor: 'pointer', fontWeight: 500 }, onClick: handleSelectAll }, "Select All"),
                    React.createElement("span", { style: { color: '#18181B', cursor: 'pointer', fontWeight: 500 }, onClick: handleDeselectAll }, "Deselect All"))),
            React.createElement("div", { style: { overflowY: 'auto', flex: 1, padding: '0 8px 12px' } }, filteredUsers.map(user => {
                const fullName = getFullName(user);
                const roleLabel = roleIdToLabel(getRoleId(user));
                const team = getTeam(user);
                const orgLabel = user.personalInfo.accessLevel === 'SweetSpot' ? 'Sweetspot' : 'Practice';
                const metaParts = [roleLabel, team, orgLabel].filter(Boolean);
                return (React.createElement("div", { key: user.id, style: {
                        display: 'flex',
                        alignItems: 'flex-start',
                        padding: '6px 8px',
                        cursor: 'pointer',
                        borderRadius: 4,
                    }, onClick: () => handleUserSelect(user) },
                    React.createElement(Checkbox, { checked: selectedUserIds.includes(user.id), size: "small", style: { padding: 4, marginRight: 8 } }),
                    React.createElement("div", null,
                        React.createElement("div", { style: { fontWeight: 600, color: '#18181B', fontFamily: 'Barlow' } }, fullName),
                        React.createElement("div", { style: { fontSize: 13, color: '#717182', fontFamily: 'Barlow' } }, metaParts.join(' • ')))));
            })))));
};
// import React, {useEffect, useState} from 'react';
// import '@fontsource/lato';
// import {getUsersForPractice} from '~/api/settings/getUsersForPractice';
// import {IUser} from '~/interfaces';
// import {
//   Checkbox, Divider,
//   IconButton, InputAdornment,
//   Menu,
//   MenuItem,
//   TextField,
// } from '@mui/material';
// import Button from '@mui/material/Button';
// import {ArrowDropDownOutlined, FilterAltOutlined, PersonOutlined, SearchOutlined} from '@mui/icons-material';
// interface Props {
//   setSelectedUsers: (selectedUsers) => void;
// }
// export const UserSelectorLayout = (props: Props) => {
//   const [users, setUsers] = useState<IUser[]>([]);
//   const [personName, setPersonName] = React.useState<string[]>([]);
//   const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
//   const [anchorEl, setAnchorEl] = React.useState<null | HTMLButtonElement>(null);
//   const open = Boolean(anchorEl);
//   const [showMenu, setShowMenu] = useState(false);
//   const [filterAnchorEl, setFilterAnchorEl] = React.useState<null | HTMLButtonElement>(null);
//   const filterOpen = Boolean(filterAnchorEl);
//   const [showFilterMenu, setShowFilterMenu] = useState(false);
//   const [query, setQuery] = useState('');
//   const [selectedFilters, setSelectedFilters] = useState<string[]>(['Show Inactive Users']);
//   const [selectedUserFilters, setSelectedUserFilters] = useState<string[]>(['SweetSpot Staff', 'Practice Staff']);
//   const [filteredUsers, setFilteredUsers] = useState<IUser[]>(users);
//   useEffect(() => {
//     // Get new data here when startDate or endDate changes
//     fetchUsers();
//   }, []);
//   const fetchUsers = async() => {
//     const { users } = await getUsersForPractice();
//     const usersArray: IUser[] = [];
//     users.map((user) => {
//       const newUser = {
//         id: user.id,
//         email: user.email,
//         personalInfo: user.personalInfo,
//         affiliations: []
//       };
//       usersArray.push(newUser);
//     });
//     usersArray.sort((a, b) => a.personalInfo.lastname.localeCompare(b.personalInfo.lastname));
//     setUsers(usersArray);
//     setFilteredUsers(usersArray);
//     const allUserIds = users.map((user) => user.id);
//     const allUserNames = users.map((user) => user.personalInfo.firstname + ' ' + user.personalInfo.lastname);
//     setSelectedUserIds(allUserIds);
//     setPersonName(allUserNames);
//     props.setSelectedUsers(allUserIds);
//   };
//   const handleSelectAll = () => {
//     if (selectedUserIds.length === users.length) {
//       // If all users are selected, deselect all
//       setSelectedUserIds([]);
//       setPersonName([]);
//       setSelectedUserFilters([]);
//     } else {
//       // If not all users are selected, select all
//       const allUserIds = users.map(user => user.id);
//       const allUserNames = users.map(user => `${user.personalInfo.firstname} ${user.personalInfo.lastname}`);
//       setSelectedUserIds(allUserIds);
//       setPersonName(allUserNames);
//       setSelectedUserFilters(['SweetSpot Staff', 'Practice Staff']);
//     }
//   };
//   const handleUserSelect = (user: IUser) => {
//     const fullName = `${user.personalInfo.firstname} ${user.personalInfo.lastname}`;
//     const userId = user.id;
//     if (selectedUserIds.includes(userId)) {
//       // Remove user from selection
//       const newSelectedUserIds = selectedUserIds.filter(id => id !== userId);
//       const newPersonNames = personName.filter(name => name !== fullName);
//       setSelectedUserIds(newSelectedUserIds);
//       setPersonName(newPersonNames);
//     } else {
//       // Add user to selection
//       const newSelectedUserIds = [...selectedUserIds, userId];
//       const newPersonNames = [...personName, fullName];
//       setSelectedUserIds(newSelectedUserIds);
//       setPersonName(newPersonNames);
//     }
//   };
//   const handleFilterSelect = (filter: string) => {
//     setSelectedFilters(prevFilters =>
//       prevFilters.includes(filter)
//         ? prevFilters.filter(f => f !== filter)
//         : [...prevFilters, filter]
//     );
//   };
//   const handleUserFilterSelect = (filter: string) => {
//     setSelectedUserFilters(prevFilters =>
//       prevFilters.includes(filter)
//         ? prevFilters.filter(f => f !== filter)
//         : [...prevFilters, filter]
//     );
//     if(filter === 'SweetSpot Staff') {
//       if (selectedUserFilters.includes(filter)) {
//         // Removing the filter: uncheck users who have 'SweetSpot' access level
//         const fUsers = users.filter(user => user.personalInfo.accessLevel === 'SweetSpot');
//         setSelectedUserIds(prevIds => prevIds.filter(id => !fUsers.some(user => user.id === id)));
//         setPersonName(prevNames => prevNames.filter(name => !fUsers.some(user => user.personalInfo.firstname + ' ' + user.personalInfo.lastname === name)));
//       } else {
//         // Adding the filter: check users who have 'SweetSpot' access level
//         const fUsers = users.filter(user => user.personalInfo.accessLevel === 'SweetSpot');
//         setSelectedUserIds(prevIds => [...new Set([...prevIds, ...fUsers.map(user => user.id)])]);
//         setPersonName(prevNames => [...new Set([...prevNames, ...fUsers.map(user => user.personalInfo.firstname + ' ' + user.personalInfo.lastname)])]);
//       }
//     }
//     if(filter === 'Practice Staff') {
//       if (selectedUserFilters.includes(filter)) {
//         // Removing the filter: uncheck users who have 'SweetSpot' access level
//         const fUsers = users.filter(user => user.personalInfo.accessLevel !== 'SweetSpot');
//         setSelectedUserIds(prevIds => prevIds.filter(id => !fUsers.some(user => user.id === id)));
//         setPersonName(prevNames => prevNames.filter(name => !fUsers.some(user => user.personalInfo.firstname + ' ' + user.personalInfo.lastname === name)));
//       } else {
//         // Adding the filter: check users who have 'SweetSpot' access level
//         const fUsers = users.filter(user => user.personalInfo.accessLevel !== 'SweetSpot');
//         setSelectedUserIds(prevIds => [...new Set([...prevIds, ...fUsers.map(user => user.id)])]);
//         setPersonName(prevNames => [...new Set([...prevNames, ...fUsers.map(user => user.personalInfo.firstname + ' ' + user.personalInfo.lastname)])]);
//       }
//     }
//   };
//   // Use useEffect to apply filters when selectedFilters change
//   useEffect(() => {
//     console.log(selectedFilters); // Now this will always log the updated filters
//     applyFilters();
//   }, [selectedFilters]); // This effect runs whenever selectedFilters change
//   // useEffect(() => {
//   //   console.log(selectedUserIds);
//   //
//   //   props.setSelectedUsers(selectedUserIds);
//   // }, [selectedUserIds]); // This effect runs whenever selectedUserIds change
//   const updateSelectedUsers = () => {
//     props.setSelectedUsers(selectedUserIds);
//     setShowMenu(false);
//     setAnchorEl(null);
//   };
//   const applyFilters = () => {
//     if(!selectedFilters.includes('Show Inactive Users')) {
//       console.log('selected filters does not include the show active which means we should not show any inactive')
//       setFilteredUsers(users.filter(user => {
//         const {inviteStatus} = user.personalInfo;
//         return (
//           (inviteStatus !== 'Inactive')
//         );
//       }));
//     } else {
//       setFilteredUsers(users.filter(user => {
//         const {inviteStatus} = user.personalInfo;
//         return (
//           (selectedFilters.includes('Show Inactive Users') && inviteStatus === 'Inactive') ||
//           (selectedFilters.includes('Show Inactive Users') && inviteStatus !== 'Inactive')
//         );
//       }));
//     }
//   };
//   return (
//     <>
//       <Button
//         style={{ backgroundColor: 'white', height: '30px', borderRadius: '4px', borderColor: '#E4E4E7', borderWidth: '2px' }}
//         endIcon={<ArrowDropDownOutlined/>}
//         onClick={(event) => {setShowMenu(true); setAnchorEl(event.currentTarget);}}>
//         {personName.length === users.length ? 'All Users' : personName.join(', ')}
//       </Button>
//       { showMenu && (
//         <div>
//           <Menu anchorEl={anchorEl}
//                 open={open} onClose={() => {setShowMenu(false); setAnchorEl(null);}}>
//             <>
//               <div className={"flex"}>
//                 <div style={{padding: '5px', color: '#000000', fontFamily: 'Barlow', fontWeight: 700, alignItems: 'center', display: 'inline-flex'}}><PersonOutlined style={{color: '#0000008A'}}/> Select Users</div>
//                 <Button variant={"contained"} style={{backgroundColor: '#2C78EB', color: '#FFFFFF', width: '20%', marginLeft: 'auto', marginRight: '5px'}} onClick={() => updateSelectedUsers()}>
//                   Apply
//                 </Button>
//               </div>
//               <br/>
//               <div style={{display: 'inline-flex', alignItems: 'center'}}>
//                 <TextField
//                   sx={{height: '30px', marginLeft: '5px'}}
//                   fullWidth
//                   variant="outlined"
//                   placeholder="Search here"
//                   value={query}
//                   onChange={(e) => {
//                     setQuery(e.target.value);
//                     setFilteredUsers(users.filter((user) =>
//                       `${user.personalInfo.firstname} ${user.personalInfo.lastname}`
//                         .toLowerCase()
//                         .includes(query.toLowerCase())
//                     ));
//                   }}
//                   InputProps={{
//                     endAdornment: (
//                       <InputAdornment position="end">
//                         <SearchOutlined />
//                       </InputAdornment>
//                     ),
//                     style: {height: '30px'}
//                   }}
//                 />
//                 <IconButton style={{float: 'right'}} onClick={(event) => {setShowFilterMenu(true); setFilterAnchorEl(event.currentTarget);}}>
//                   <FilterAltOutlined style={{color: '#2C78EB'}}/>
//                 </IconButton>
//               </div>
//               { showFilterMenu && (
//                 <div>
//                   <Menu anchorEl={filterAnchorEl}
//                         open={filterOpen} onClose={() => {setShowFilterMenu(false); setFilterAnchorEl(null);}}>
//                     {['Show Inactive Users'].map(filter => (
//                       <MenuItem key={filter} onClick={() => handleFilterSelect(filter)}>
//                         <Checkbox checked={selectedFilters.includes(filter)} />
//                         {filter}
//                       </MenuItem>
//                     ))}
//                   </Menu>
//                 </div>
//               )}
//               <br/>
//               <MenuItem key='select-all' value='select-all' onClick={handleSelectAll}>
//                 <Checkbox checked={selectedUserIds.length === users.length} />
//                 {personName.length === users.length ? 'Select All' : 'Select All'}
//               </MenuItem>
//               {['SweetSpot Staff', 'Practice Staff'].map(filter => (
//                 <MenuItem key={filter} onClick={() => handleUserFilterSelect(filter)}>
//                   <Checkbox checked={selectedUserFilters.includes(filter)} />
//                   {filter}
//                 </MenuItem>
//               ))}
//               <Divider/>
//               <div className='overflow-auto'>
//                 {filteredUsers.map((user) => (
//                   <MenuItem
//                     key={user.id}
//                     value={`${user.personalInfo.firstname} ${user.personalInfo.lastname}`}
//                     onClick={() => handleUserSelect(user)}
//                   >
//                     <Checkbox
//                       checked={selectedUserIds.includes(user.id)}
//                     />
//                     {user.personalInfo.firstname + ' ' + user.personalInfo.lastname}
//                   </MenuItem>
//                 ))}
//               </div>
//             </>
//           </Menu>
//         </div>
//       )}
//     </>
//   );
// };
